/**
 * Wallet's bottom bar: Home · Records · [ + ] · Statistics · More.
 *
 * WHY A BOTTOM BAR AND NOT THE DESKTOP TABS
 * -----------------------------------------
 * Wallet is a phone app and its centre button is the most-pressed pixel in it.
 * A top nav puts the primary action at the far end of a thumb's reach; this
 * puts it under it. On desktop the bar STAYS rather than turning back into a
 * row of top tabs: the surface spans the window but its contents are capped at
 * the same `max-w-5xl` column the content uses, so the tabs sit under the
 * content instead of stranded at the screen edges. Two navigations for one app
 * is how the two drift apart.
 *
 * WHAT IT MAY LINK TO
 * -------------------
 * Three registry pages that already exist (`dashboard`, `records`, `analytics`)
 * and two things that are not destinations at all: the entry overlay and the
 * More sheet. It adds NO route. Each tab is drawn only when the user's
 * `PagePermission` row allows the page, and the centre button only when they
 * may see a page an entry writes to.
 *
 * SIXTY FRAMES
 * ------------
 * The active-tab pill is a `layoutId` shared element, so framer-motion animates
 * it with a transform rather than by moving a border. Presses are `scale`
 * transforms. Nothing here animates a layout property.
 */
import { useMemo } from "react";
import Link from "@/compat/link";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, type LucideIcon } from "lucide-react";
import { findPageByKey, type PageKey } from "@/lib/rbac/pages";
import { cn } from "@/lib/utils";
import { activeTab, MORE_ICON, PRIMARY_TABS, type PrimaryTab } from "./nav";

interface Tab extends PrimaryTab {
  icon: LucideIcon;
}

export interface BottomNavProps {
  visible: ReadonlySet<PageKey>;
  /** False when no page an entry writes to is visible to this user. */
  canAdd: boolean;
  onAdd(): void;
  onMore(): void;
  moreOpen: boolean;
}

export function BottomNav({ visible, canAdd, onAdd, onMore, moreOpen }: BottomNavProps) {
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const current = moreOpen ? "more" : activeTab(pathname);

  const tabs = useMemo<Tab[]>(
    () =>
      PRIMARY_TABS.filter((tab) => visible.has(tab.pageKey)).map((tab) => ({
        ...tab,
        icon: findPageByKey(tab.pageKey)?.icon ?? MORE_ICON,
      })),
    [visible],
  );

  // Two on the left of the button, the rest on its right — the button stays
  // centred even when a permission removes a tab.
  const left = tabs.filter((tab) => tab.key !== "stats");
  const right = tabs.filter((tab) => tab.key === "stats");

  const tabCls = (active: boolean) =>
    cn(
      "relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors",
      active ? "text-[var(--cp-accent)]" : "text-[var(--cp-ink-3)] hover:text-[var(--cp-ink-2)]",
    );

  const renderTab = (tab: Tab) => {
    const active = current === tab.key;
    return (
      <Link
        key={tab.key}
        href={tab.href}
        aria-current={active ? "page" : undefined}
        className={tabCls(active)}
      >
        {active && (
          <motion.span
            layoutId="wallet-tab-pill"
            aria-hidden="true"
            className="absolute top-0 h-[3px] w-6 rounded-full bg-[var(--cp-accent)]"
            transition={
              reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 38 }
            }
          />
        )}
        <tab.icon className="h-5 w-5" strokeWidth={active ? 2.3 : 2} />
        <span className="max-w-full truncate px-1 text-[10px] font-medium leading-none">
          {tab.label}
        </span>
      </Link>
    );
  };

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--cp-line)] bg-[var(--cp-surface)] pb-[env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto flex h-[60px] max-w-5xl items-stretch px-1">
        {left.map(renderTab)}

        {/* The centre affordance. Raised out of the bar the way Wallet's is —
            a transform, so it costs nothing per frame. */}
        {canAdd ? (
          <div className="relative flex w-[76px] shrink-0 items-start justify-center">
            {/* The elevated-shadow layer, separate from the button so only its
                OPACITY animates (see `Lift` in `lib/motion` for the same
                pattern) — a `box-shadow` transition repaints every frame,
                this doesn't. Fades in on hover or while pressed; the button's
                own static shadow keeps it grounded the rest of the time. */}
            {!reduceMotion && (
              <motion.span
                aria-hidden="true"
                className="pointer-events-none absolute -mt-5 h-14 w-14 rounded-full shadow-[0_20px_34px_-10px_rgba(0,0,0,0.5)]"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                whileTap={{ opacity: 1 }}
                transition={{ duration: 0.16 }}
                style={{ willChange: "opacity" }}
              />
            )}
            <motion.button
              type="button"
              onClick={onAdd}
              aria-label="Add a record"
              whileTap={reduceMotion ? undefined : { scale: 0.9 }}
              transition={
                reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 600, damping: 30 }
              }
              className="relative -mt-5 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-[var(--cp-shadow-lg)] ring-4 ring-[var(--cp-surface)] focus-visible:outline-none focus-visible:ring-[6px] focus-visible:ring-[var(--cp-accent-wash)]"
              style={{
                background: "linear-gradient(135deg, var(--cp-accent-2), var(--cp-accent))",
                willChange: "transform",
              }}
            >
              <Plus className="h-7 w-7" strokeWidth={2.5} />
            </motion.button>
          </div>
        ) : (
          <div className="w-[76px] shrink-0" aria-hidden="true" />
        )}

        {right.map(renderTab)}

        <button type="button" onClick={onMore} aria-haspopup="dialog" aria-expanded={moreOpen} className={tabCls(current === "more")}>
          {current === "more" && (
            <motion.span
              layoutId="wallet-tab-pill"
              aria-hidden="true"
              className="absolute top-0 h-[3px] w-6 rounded-full bg-[var(--cp-accent)]"
              transition={
                reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 38 }
              }
            />
          )}
          <MORE_ICON className="h-5 w-5" strokeWidth={current === "more" ? 2.3 : 2} />
          <span className="max-w-full truncate px-1 text-[10px] font-medium leading-none">More</span>
        </button>
      </div>
    </nav>
  );
}
